import { useRef, useState, useCallback, useEffect, type ChangeEvent, type FormEvent } from "react";
import { Link, useLocation } from "wouter";
import { Upload, Globe, ArrowRight } from "lucide-react";
import { Shell } from "@/components/layout/Shell";
import { useSeo } from "@/hooks/use-seo";
import { getColorOfTheDay, COLOR_LIST } from "@/data/colors";
import { setPendingFile } from "@/lib/pending-upload";
import { UnderstandingColorGuide } from "@/components/UnderstandingColorGuide";

// ── Color Ribbon ──────────────────────────────────────────────────────────────
// Horizontal strip of all 175 colors; today's color auto-scrolled to center.

function ColorRibbon({ colorOfDayId }: { colorOfDayId: string }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const todayRef  = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const container = scrollRef.current;
    const el = todayRef.current;
    if (!container || !el) return;

    const center = () => {
      container.scrollLeft = el.offsetLeft - container.offsetWidth / 2 + el.offsetWidth / 2;
    };

    center();
    window.addEventListener("resize", center);
    return () => window.removeEventListener("resize", center);
  }, []);

  return (
    <div
      ref={scrollRef}
      className="w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden rounded-2xl border border-border bg-card/50"
    >
      <div className="flex items-center gap-1.5 px-4 py-3 min-w-max">
        {COLOR_LIST.map((color) => {
          const isToday = color.id === colorOfDayId;
          return (
            <span key={color.id} ref={isToday ? todayRef : undefined} className="flex-shrink-0">
              <Link
                href={`/learn/${color.id}`}
                title={isToday ? `${color.name} — Color of the Day` : color.name}
                aria-label={isToday ? `${color.name} — Color of the Day` : color.name}
                className={`block rounded-full transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-primary hover:scale-125 hover:shadow-md ${
                  isToday
                    ? "w-11 h-11 ring-[3px] ring-offset-2 ring-primary shadow-lg scale-110"
                    : "w-7 h-7 opacity-70 hover:opacity-100"
                }`}
                style={{ backgroundColor: color.hex }}
              />
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ── Instant-action tool cards (Group 1) ──────────────────────────────────────

interface FileCardProps {
  href: string;
  title: string;
  description: string;
  accentBg: string;   // Tailwind bg class for the left accent bar
  accentBtn: string;  // Tailwind classes for the upload button
}

function FileCard({ href, title, description, accentBg, accentBtn }: FileCardProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [, navigate] = useLocation();

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // Reset so the same file can be chosen again next time.
      e.target.value = "";
      setPendingFile(file);
      navigate(href);
    },
    [href, navigate],
  );

  return (
    <div className="flex flex-col rounded-2xl border border-border bg-card overflow-hidden hover:shadow-lg hover:border-border/60 transition-all duration-200">
      {/* accent bar */}
      <div className={`h-1 w-full ${accentBg}`} />
      <div className="flex flex-col flex-1 p-4 gap-2">
        <h3 className="font-semibold text-sm leading-tight">{title}</h3>
        <p className="hidden sm:block text-xs text-muted-foreground leading-snug flex-1">{description}</p>
        <input
          type="file"
          accept="image/*"
          ref={inputRef}
          className="sr-only"
          onChange={handleChange}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`mt-auto flex items-center justify-center gap-1.5 w-full py-2 rounded-xl text-xs font-medium transition-opacity hover:opacity-90 ${accentBtn}`}
        >
          <Upload className="w-3.5 h-3.5" />
          Upload a Photo
        </button>
      </div>
    </div>
  );
}

function ScannerCard() {
  const [url, setUrl] = useState("");
  const [, navigate] = useLocation();

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const v = url.trim();
      if (!v) return;
      navigate(`/scan?url=${encodeURIComponent(v)}`);
    },
    [url, navigate],
  );

  return (
    <div className="flex flex-col rounded-2xl border border-border bg-card overflow-hidden hover:shadow-lg hover:border-border/60 transition-all duration-200">
      <div className="h-1 w-full bg-amber-500" />
      <div className="flex flex-col flex-1 p-4 gap-2">
        <h3 className="font-semibold text-sm leading-tight">Website Scanner</h3>
        <p className="hidden sm:block text-xs text-muted-foreground leading-snug flex-1">
          Extract every color from any website — with psychology meanings and hex codes.
        </p>
        <form onSubmit={handleSubmit} className="mt-auto flex gap-1.5">
          <div className="relative flex-1 min-w-0">
            <Globe className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              required
              spellCheck={false}
              className="w-full pl-7 pr-2 py-2 rounded-xl border border-border bg-background text-xs placeholder:text-muted-foreground/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-shadow"
            />
          </div>
          <button
            type="submit"
            className="flex-shrink-0 px-3 py-2 rounded-xl bg-amber-500 text-white text-xs font-medium hover:opacity-90 transition-opacity"
          >
            Scan
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Explore link cards (Group 2) ─────────────────────────────────────────────

interface ExploreCardProps {
  href: string;
  title: string;
  description: string;
  accentText: string;
}

function ExploreCard({ href, title, description, accentText }: ExploreCardProps) {
  return (
    <Link
      href={href}
      className={`group flex flex-col gap-1 p-3 sm:p-4 rounded-2xl border border-border bg-card hover:shadow-md transition-all duration-200 hover:border-border/60`}
    >
      <span className={`text-xs sm:text-sm font-semibold transition-colors ${accentText}`}>{title}</span>
      <span className="hidden sm:block text-xs text-muted-foreground leading-snug flex-1">{description}</span>
      <span className="flex items-center gap-1 text-xs text-muted-foreground mt-auto">
        Go <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </Link>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function Home() {
  const colorOfDay = getColorOfTheDay();

  useSeo({
    title: "Understanding Color: Theory, Meaning & Uses | Mychromamind",
    description:
      "Understand color theory, color psychology, cultural meaning, palette design, branding, and accessibility with practical examples and interactive tools.",
    path: "/",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": "https://mychromamind.com/#website",
          name: "Mychromamind",
          url: "https://mychromamind.com",
          description:
            "Explore a practical color theory and psychology guide, interactive color tools, and a library of color meanings.",
        },
        {
          "@type": "Organization",
          "@id": "https://mychromamind.com/#organization",
          name: "Mychromamind",
          url: "https://mychromamind.com",
          logo: "https://mychromamind.com/favicon.svg",
        },
        {
          "@type": "ItemList",
          name: "Site Navigation",
          itemListElement: [
            { "@type": "SiteNavigationElement", position: 1, name: "Color Quiz",       url: "https://mychromamind.com/quiz/personality" },
            { "@type": "SiteNavigationElement", position: 2, name: "Website Scanner",  url: "https://mychromamind.com/scan" },
            { "@type": "SiteNavigationElement", position: 3, name: "Color Library",    url: "https://mychromamind.com/learn" },
            { "@type": "SiteNavigationElement", position: 4, name: "Color Insights",   url: "https://mychromamind.com/insights" },
            { "@type": "SiteNavigationElement", position: 5, name: "Color Grabber",    url: "https://mychromamind.com/color-grabber" },
            { "@type": "SiteNavigationElement", position: 6, name: "Aura Scanner",     url: "https://mychromamind.com/aura-scanner" },
            { "@type": "SiteNavigationElement", position: 7, name: "Daily Pulse",      url: "https://mychromamind.com/pulse" },
            { "@type": "SiteNavigationElement", position: 8, name: "Background Maker",    url: "https://mychromamind.com/background-maker" },
            { "@type": "SiteNavigationElement", position: 9, name: "Paint Visualizer",     url: "https://mychromamind.com/paint-visualizer" },
          ],
        },
      ],
    },
  });

  return (
    <Shell>
      <div className="flex-1 flex flex-col px-4 sm:px-6 py-4 gap-3 max-w-5xl mx-auto w-full">

        {/* ── Hero ── */}
        <div className="text-center space-y-1">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted text-xs font-medium tracking-wide uppercase text-foreground">
            <span className="w-2 h-2 rounded-full bg-primary" />
            Color Psychology Explorer
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif text-foreground leading-tight">
            What do your <span className="text-primary italic">instincts</span> look like?
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-xl mx-auto">
            Upload a photo, scan a site, or explore 175 colors — all grounded in real color psychology.
          </p>
        </div>

        {/* ── Color of the Day Ribbon ── */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between px-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Color of the Day</span>
            <Link
              href={`/learn/${colorOfDay.id}`}
              className="flex items-center gap-1.5 text-xs font-medium hover:text-primary transition-colors"
            >
              <span
                className="w-4 h-4 rounded-full ring-1 ring-border inline-block"
                style={{ backgroundColor: colorOfDay.hex }}
              />
              {colorOfDay.name}
            </Link>
          </div>
          <ColorRibbon colorOfDayId={colorOfDay.id} />
        </div>

        {/* ── Group 1: Instant Tools ── */}
        <div className="space-y-2">
          <div className="flex items-baseline gap-2 px-1">
            <h2 className="text-sm font-semibold">Try it instantly</h2>
            <span className="hidden sm:block text-xs text-muted-foreground">upload a photo or enter a URL — no sign-in needed</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <FileCard
              href="/background-maker"
              title="Background Maker"
              description="Turn any photo's colors into a downloadable background — gradients, low-poly, watercolor, and more."
              accentBg="bg-cyan-500"
              accentBtn="bg-cyan-500 text-white"
            />
            <FileCard
              href="/aura-scanner"
              title="Aura Scanner"
              description="Extract your top 3 dominant colors from a photo, with a full personality read on your aura."
              accentBg="bg-fuchsia-500"
              accentBtn="bg-fuchsia-500 text-white"
            />
            <FileCard
              href="/color-grabber"
              title="Color Grabber"
              description="Detect every color in a photo, grouped by name with hex codes and a full palette analysis."
              accentBg="bg-orange-500"
              accentBtn="bg-orange-500 text-white"
            />
            <ScannerCard />
          </div>
        </div>

        {/* ── Group 2: Explore Tools ── */}
        <div className="space-y-2">
          <h2 className="text-sm font-semibold px-1">Explore</h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            <ExploreCard
              href="/quiz/personality"
              title="Personality Read"
              description="7-round instinctive color quiz that reveals your emotional baseline."
              accentText="group-hover:text-primary"
            />
            <ExploreCard
              href="/quiz/decision"
              title="Decision Guidance"
              description="Let your subconscious map a choice through color psychology."
              accentText="group-hover:text-blue-500"
            />
            <ExploreCard
              href="/pulse"
              title="Mood Pulse"
              description="Daily one-color check-in — track how your instincts shift over time."
              accentText="group-hover:text-emerald-500"
            />
            <ExploreCard
              href="/decision-matrix"
              title="Decision Matrix"
              description="Map a dilemma across fears, aspirations, and hesitations."
              accentText="group-hover:text-rose-500"
            />
            <ExploreCard
              href="/synergy"
              title="Color Synergy"
              description="Compare your dominant color with a friend's compatibility matrix."
              accentText="group-hover:text-violet-500"
            />
            <ExploreCard
              href="/learn"
              title="Color Library"
              description="Browse 175 colors with psychology meanings and decision guidance."
              accentText="group-hover:text-sky-500"
            />
          </div>
        </div>

        {/* ── Group 3: Build ── */}
        <div className="space-y-2">
          <div className="flex items-baseline gap-2 px-1">
            <h2 className="text-sm font-semibold">Build</h2>
            <span className="hidden sm:block text-xs text-muted-foreground">test your palette across real spaces and UI components</span>
          </div>
          <ExploreCard
            href="/color-sandbox"
            title="Color Psychology Sandbox"
            description="Live palette tester across SaaS, e-commerce, mobile &amp; blog layouts — with WCAG contrast checker, color-blindness simulator, psychological profile engine, and CSS/Tailwind export."
            accentText="group-hover:text-indigo-500"
          />
          <ExploreCard
            href="/paint-visualizer"
            title="Room Paint Visualizer"
            description="Upload a photo of your room or use your live camera, tap a wall, and instantly preview any paint color on it. Includes color psychology insights and a paint quantity &amp; cost calculator."
            accentText="group-hover:text-orange-500"
          />
        </div>

        <UnderstandingColorGuide />

      </div>
    </Shell>
  );
}
